import { FunctionComponent, useState } from "react";

import { Snackbar } from "@mui/material";

import { Modal } from "@/components";

import { ButtonColor } from "@/components/Button/types";

import { useApplySegmentChangesMutation } from "@/pages/Private/redux/knowledgeDb/knowledgeDb.api";

import { SegmentChangeItem } from "../../KnowledgeDb/schema/knowledgeDbApi";

interface ModalSegmentSyncProps {
	isOpened: boolean;
	onClose: () => void;
	changes: SegmentChangeItem[];
	pendingSyncId?: number | null;
}

export const changeBadge = (changeType: string) => {
	switch (changeType) {
		case "NEW":
			return (
				<span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
					NEW
				</span>
			);
		case "REMOVED":
			return (
				<span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
					REMOVED
				</span>
			);
		case "CHANGED":
			return (
				<span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
					CHANGED
				</span>
			);
		default:
			return null;
	}
};

export const changeDescription = (change: SegmentChangeItem) => {
	switch (change.changeType) {
		case "NEW":
			return `"${change.personaCategory ?? ""}" × "${change.companyCategory ?? ""}" — Will generate new ICP`;
		case "REMOVED": {
			const segment = change.companyCategory ?? change.personaCategory ?? "";
			const count = change.affectedIcpIds?.length ?? 0;

			return `"${segment}" — affects ${count} ICP${count !== 1 ? "s" : ""} — Will archive ICPs`;
		}

		case "CHANGED": {
			const segment = change.personaCategory ?? change.companyCategory ?? "";
			const count = change.affectedIcpIds?.length ?? 0;

			return `"${segment}" description changed — affects ${count} ICP${count !== 1 ? "s" : ""} — Will regenerate ICPs`;
		}

		default:
			return "";
	}
};

/**
 * Filters segment changes to only include ICP-forming changes,
 * i.e. changes where both a persona AND a company are present.
 * A company category alone does not form an ICP.
 */
export const filterIcpChanges = (changes: SegmentChangeItem[]): SegmentChangeItem[] =>
	changes.filter(
		(change) =>
			(change.personaCategory || change.scorePersonaCategoryId) &&
			(change.companyCategory || change.scoreCompanyCategoryId)
	);

export const ModalSegmentSync: FunctionComponent<ModalSegmentSyncProps> = ({
	isOpened,
	onClose,
	changes = [],
	pendingSyncId = null,
}) => {
	const [applySegmentChanges, { isLoading }] = useApplySegmentChangesMutation();
	const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);

	const handleAction = async (action: "GENERATE_NOW" | "REMIND_LATER" | "SKIP") => {
		try {
			await applySegmentChanges({
				action,
				changes,
				pendingSyncId,
			}).unwrap();

			const messages: Record<string, string> = {
				GENERATE_NOW: "Knowledge DB sync scheduled",
				REMIND_LATER: "We'll remind you next time",
				SKIP: "You can update ICPs manually in the Knowledge DB",
			};

			setSnackbarMessage(messages[action]);
			onClose();
		} catch (e) {
			console.error(e);
		}
	};

	return (
		<>
			<Modal
				handleClose={onClose}
				handleSave={() => handleAction("GENERATE_NOW")}
				handleSecondSave={() => handleAction("REMIND_LATER")}
				handleThirdSave={() => handleAction("SKIP")}
				isLoading={isLoading}
				isOpened={isOpened}
				secondSubmitButtonText="Remind Me Later"
				size="md"
				submitButtonColor={ButtonColor.ACTION}
				submitButtonText="Generate Now"
				thirdSubmitButtonText="Skip"
				title="Knowledge DB Segment Changes Detected"
			>
				<div className="flex flex-col gap-3">
					<p className="text-sm text-gray-600">
						Your Lead Scoring changes affect the Knowledge Database. Review the changes below and
						choose how to proceed.
					</p>
					<div className="flex flex-col gap-2">
						{changes.map((change, index) => (
							<div
								key={index}
								className="flex flex-row items-start gap-3 p-3 rounded-lg bg-gray-50 border border-gray-200"
							>
								<div className="flex-shrink-0 mt-0.5">{changeBadge(change.changeType)}</div>
								<p className="text-sm text-gray-700">{changeDescription(change)}</p>
							</div>
						))}
					</div>
				</div>
			</Modal>
			{snackbarMessage ? (
				<Snackbar
					autoHideDuration={3000}
					message={snackbarMessage}
					open={!!snackbarMessage}
					onClose={() => setSnackbarMessage(null)}
				/>
			) : null}
		</>
	);
};
